<!-- src/components/forms/completed/SupportRequestCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- STAGE 1: CORE CONTEXT -->
    <section>
      <h3 class="section-title">Stage 1: Requestor & Contract Identity</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div v-for="field in personnelFields" :key="field.key" class="space-y-1">
          <p class="micro-label text-slate-400">{{ field.label }}</p>
          <a v-if="field.key === 'requestorEmail'" :href="'mailto:' + getDisplayValue(field.key)" class="text-[13px] font-black text-[#20FCF1] hover:underline lowercase tracking-tight">{{ getDisplayValue(field.key) }}</a>
          <a v-else-if="field.key === 'requestorPhone'" :href="'tel:' + getDisplayValue(field.key)" class="text-[13px] font-black text-[#0A1630] hover:text-[#FD5E00] uppercase tracking-tight">{{ getDisplayValue(field.key) }}</a>
          <p v-else class="text-[13px] font-black text-[#0A1630] uppercase tracking-tight">{{ getDisplayValue(field.key) }}</p>
        </div>
      </div>
    </section>

    <!-- STAGE 1: SITE INFRASTRUCTURE -->
    <section>
      <h3 class="section-title">Stage 1: Site Location & Infrastructure</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="field in infrastructureFields" :key="field.key" class="bg-white border border-slate-100 p-6 rounded-[32px] shadow-sm">
          <p class="micro-label text-slate-300 mb-2">{{ field.label }}</p>
          <a v-if="field.key === 'siteWhat3Words'" :href="'https://what3words.com/' + cleanW3W(getDisplayValue(field.key))" target="_blank" class="text-sm font-black text-[#20FCF1] hover:underline tracking-tight flex items-center gap-2"><MapIcon class="w-3 h-3" />{{ getDisplayValue(field.key) }}</a>
          <p v-else class="text-sm font-black text-[#0A1630] uppercase tracking-tight">{{ getDisplayValue(field.key) }}</p>
        </div>
        <div class="md:col-span-2 lg:col-span-4 bg-[#0A1630] p-6 rounded-[32px] shadow-lg border-b-4 border-[#20FCF1]">
          <p class="micro-label text-[#20FCF1] mb-2">Tracks Affected / Infrastructure Impact</p>
          <p class="text-sm font-bold text-white uppercase tracking-wide">{{ getDisplayValue('tracksAffected') }}</p>
        </div>
      </div>
    </section>

    <section>
      <h3 class="section-title">Stage 1: Work Scope & Objectives</h3>
      <div class="bg-white border border-slate-100 p-8 md:p-10 rounded-[40px] shadow-sm">
          <p class="micro-label text-slate-300 mb-3 uppercase tracking-widest">Detailed Scope of Works</p>
          <p class="text-sm font-medium text-slate-700 leading-relaxed italic border-l-4 border-slate-100 pl-6">{{ getDisplayValue('workScope') }}</p>
      </div>
    </section>

    <!-- STAGE 1: SHIFT LOGISTICS -->
    <section>
      <h3 class="section-title">Stage 1: Requested Resource Allocation</h3>
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div v-for="idx in [1, 2, 3]" :key="idx" class="min-w-0">
          <div v-if="hasShift(idx)" class="h-full bg-white border border-slate-100 rounded-[32px] shadow-sm flex flex-col overflow-hidden">
            <div class="bg-[#0A1630] p-5 border-b border-white/5">
              <h3 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em]">Requirement: Shift {{ idx }}</h3>
            </div>
            <div class="p-6 space-y-6 flex-grow">
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1"><p class="text-[8px] font-black text-slate-300 uppercase">Start</p><p class="text-[10px] font-bold text-[#0A1630]">{{ formatDate(submissionData['shiftStartDate' + idx]) }}</p></div>
                <div class="space-y-1"><p class="text-[8px] font-black text-slate-300 uppercase">Finish</p><p class="text-[10px] font-bold text-[#0A1630]">{{ formatDate(submissionData['shiftEndDate' + idx]) }}</p></div>
              </div>
              <div class="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3 shadow-inner">
                <p class="text-[8px] font-black text-slate-400 uppercase">Resource: {{ formatResource(submissionData['resourceRequired' + idx]) }}</p>
                <p class="text-[10px] font-black text-[#0A1630]">Qty: {{ submissionData['quantity' + idx] }} • Level: {{ submissionData['level' + idx] || 'N/A' }}</p>
              </div>
              <div class="space-y-1">
                <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest">Site Liaison</p>
                <p class="text-[11px] font-black text-[#0A1630] uppercase leading-none">{{ submissionData['siteContactName' + idx] }}</p>
                <a :href="'tel:' + submissionData['siteContactNumber' + idx]" class="text-[10px] font-bold text-[#20FCF1] hover:underline">{{ submissionData['siteContactNumber' + idx] }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- STAGE 2: RESPONSE SEAL -->
    <section v-if="submissionData.internalResponse" class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden animate-fadeIn">
      <div class="absolute top-0 right-0 p-8 opacity-10"><CheckBadgeIcon class="h-32 w-32 text-white" /></div>
      <div class="relative z-10">
        <h3 class="text-xl font-black uppercase text-[#20FCF1] mb-8 tracking-tight">Stage 2: Official Authorisation Response</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-10 text-sm font-bold">
          <div><p class="micro-label text-slate-500 mb-1 uppercase tracking-widest">Lead Authoriser</p><p class="uppercase">{{ submissionData.internalResponse.providerLead }}</p></div>
          <div><p class="micro-label text-slate-500 mb-1 uppercase tracking-widest">Registry Reference</p><p class="uppercase">{{ submissionData.internalResponse.quoteReference }}</p></div>
          <div v-if="submissionData.internalResponse.quotePdfUrl">
            <p class="micro-label text-slate-500 mb-2 uppercase tracking-widest">Authorisation PDF</p>
            <a :href="submissionData.internalResponse.quotePdfUrl" target="_blank" class="inline-flex items-center px-6 py-2 bg-[#20FCF1] text-[#0A1630] rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg">View Quote</a>
          </div>
        </div>
      </div>
    </section>

    <!-- STAGE 3: CLIENT DECISION SEAL -->
    <section v-if="submissionData.clientDecision" class="animate-fadeIn">
       <div :class="submissionData.clientDecision.status === 'Authorised' ? 'bg-[#EBFDF8] border-[#20FCF1]' : 'bg-rose-50 border-rose-200'" class="p-10 rounded-[40px] border-2 shadow-xl relative overflow-hidden">
          <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <p class="micro-label text-slate-400 mb-2 uppercase tracking-widest">Stage 3: Client Directive Record</p>
              <h3 :class="submissionData.clientDecision.status === 'Authorised' ? 'text-[#0A1630]' : 'text-rose-600'" class="text-3xl font-black uppercase tracking-tighter leading-none">
                {{ submissionData.clientDecision.status === 'Authorised' ? 'GO - WORK AUTHORISED' : 'ABORT - QUOTE DECLINED' }}
              </h3>
              <p class="text-[10px] font-black text-slate-400 uppercase mt-3 tracking-widest">Decision Timestamp: {{ formatDate(submissionData.clientDecision.decidedAt) }}</p>
            </div>
            <div class="shrink-0">
               <div :class="submissionData.clientDecision.status === 'Authorised' ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-rose-500 text-white'" class="h-20 w-20 rounded-3xl flex items-center justify-center text-4xl shadow-lg font-black">{{ submissionData.clientDecision.status === 'Authorised' ? '✓' : '✕' }}</div>
            </div>
          </div>
       </div>
    </section>

    <!-- STAGE 4: PERSONNEL DEPLOYMENT REGISTRY (Repeats Shift Data) -->
    <section v-if="submissionData.staffDeployment" class="animate-fadeIn mt-12">
      <h3 class="section-title">Stage 4: Final Personnel Deployment Registry</h3>
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div v-for="idx in [1, 2, 3]" :key="idx" class="min-w-0">
          <div v-if="submissionData['shiftStartDate' + idx]" class="h-full bg-[#0A1630] p-8 rounded-[40px] shadow-2xl relative overflow-hidden group border-b-4 border-[#20FCF1]">
            <div class="relative z-10 space-y-6">
              <div class="flex items-center justify-between">
                <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Shift {{ idx }} Deployment</p>
                <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
              </div>

              <!-- REPEATED SHIFT DATA FOR CONTEXT -->
              <div class="bg-white/5 p-4 rounded-2xl border border-white/5 space-y-3">
                 <div class="flex justify-between items-center"><p class="text-[8px] font-black text-slate-500 uppercase">Requirement</p><p class="text-[9px] font-black text-white uppercase tracking-tighter">{{ formatResource(submissionData['resourceRequired' + idx]) }}</p></div>
                 <div class="flex justify-between items-center"><p class="text-[8px] font-black text-slate-500 uppercase">Timing</p><p class="text-[9px] font-bold text-[#20FCF1]">{{ formatDate(submissionData['shiftStartDate' + idx]) }}</p></div>
              </div>

              <div class="space-y-4 pt-2">
                <div class="space-y-1">
                  <p class="text-[8px] font-black text-slate-500 uppercase tracking-widest">Assigned Personnel</p>
                  <p class="text-lg font-black text-white uppercase tracking-tight">{{ submissionData.staffDeployment['staffName' + idx] || 'PENDING' }}</p>
                </div>
                <div class="space-y-1">
                  <p class="text-[8px] font-black text-slate-500 uppercase tracking-widest">Direct Contact</p>
                  <a v-if="submissionData.staffDeployment['staffPhone' + idx]" :href="'tel:' + submissionData.staffDeployment['staffPhone' + idx]" class="text-sm font-bold text-[#20FCF1] hover:underline tracking-widest transition-colors">{{ submissionData.staffDeployment['staffPhone' + idx] }}</a>
                  <p v-else class="text-sm font-bold text-slate-600 tracking-widest">NO PHONE RECORDED</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { CheckBadgeIcon, MapIcon } from '@heroicons/vue/24/outline';
const props = defineProps({ submissionData: { type: Object, required: true }, formatDate: { type: Function, required: true } });
const personnelFields = [
  { label: 'Client Organization', key: 'companyDisplayName' }, { label: 'Technical Discipline', key: 'discipline' },
  { label: 'Operational Region', key: 'region' }, { label: 'Requesting Authority', key: 'requestorName' },
  { label: 'Lead Phone', key: 'requestorPhone' }, { label: 'Lead Email', key: 'requestorEmail' }, { label: 'Contract Reference', key: 'sreContractRef' }
];
const infrastructureFields = [ { label: 'ELR Code', key: 'elr' }, { label: 'Mileage From', key: 'mileageFrom' }, { label: 'Mileage To', key: 'mileageTo' }, { label: 'What3Words Site Ref', key: 'siteWhat3Words' } ];
const hasShift = (idx) => !!props.submissionData[`shiftStartDate${idx}`];
const getDisplayValue = (key) => props.submissionData[key] || 'NOT SPECIFIED';
const cleanW3W = (str) => str?.replace(/[/]/g, '').trim() || '';
const formatResource = (text) => text?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A';
</script>

<style scoped>
.section-title { @apply text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b-2 border-slate-100 pb-3 ml-2; }
.micro-label { @apply font-black text-slate-300 uppercase text-[9px] tracking-widest; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>